import React, { useState, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, trim, forEach, toLower, split } from 'lodash';
import InputWithIcon from 'common-ui-components/dist/components/Atoms/InputWithIcon';
import TextArea from 'common-ui-components/dist/components/Atoms/TextArea';
import Button from 'common-ui-components/dist/components/Atoms/Button';
import Anchor from 'common-ui-components/dist/components/Atoms/Anchor';
import RequiredText from 'common-ui-components/dist/components/Atoms/RequiredText';
import validate from 'common-ui-components/dist/core/utils/validator';
import {
  getBasePriceValue,
  getCustomerPriceValue,
} from 'common-ui-components/dist/components/Organisms/SKUTiles/SKUTiles.utils';
import { getLabelsWithAsterisk, isEProcurementApp } from '../../../utils/utils';
import {
  EMAIL_INPUT_MAX_LENGTH,
  RECIPIENT_EMAIL_MAX_LENGTH,
  RECIPIENT_MAX_EMAIL_ACCEPT,
  WHITESPACE_REGEX,
} from '../../../constants';
import TermsConditions from '../../templates/Checkout/TermsConditions';
import Captcha from '../Captcha';
import './ShareCart.scss';

const ShareCartForm = props => {
  const {
    labels,
    config,
    onShareCartSubmit,
    setShowShareCartModal,
    isoCode,
    cartItems,
    includeTaxWithPrice,
    isUserLoggedIn,
    userEmail = '',
  } = props;
  const [inputSenderEmail, setSenderEmail] = useState({
    touched: false,
    valid: validate(userEmail, { isRequired: true, isEmail: true }),
    value: userEmail,
    errorMsg: '',
  });
  const [inputRecipientEmail, setRecipientEmail] = useState({
    touched: false,
    valid: false,
    value: '',
    errorMsg: '',
  });
  const [shareCartMessage, setShareCartMessage] = useState('');
  const [reCaptchaStatus, setReCaptchaStatus] = useState({
    valid: false,
    showErrorMsg: false,
  });
  const {
    requiredField,
    yourEmailAddress,
    recipientEmailAddress,
    message,
    shareCartTermsCondition,
    termsOfUse,
    watersPrivacyNotice,
    shareCart,
    cancel,
    emailValidateMessage,
    captchaErrorMessage,
    shareCartSameEmailMessage,
    optional,
    multipleEmailMessage,
    recipientEmailPlaceholder,
  } = labels;
  const { termsOfUseLink, watersPrivacyNoticeLink } = config;

  const setEmailStatus = (
    value,
    valid,
    touched = true,
    errorMsg = '',
    callBack = () => {
      /* default callBack handler */
    }
  ) => {
    const data = {
      value,
      valid,
      touched,
      errorMsg,
    };
    callBack(data);
  };

  useEffect(() => {
    const { value } = inputSenderEmail;
    if (!isEmpty(userEmail) && userEmail !== value) {
      const valid = validate(userEmail, { isRequired: true, isEmail: true });
      setEmailStatus(userEmail, valid, true, emailValidateMessage, setSenderEmail);
    }
  }, [userEmail]);

  const handleOnChange = (e, callBack) => {
    let {
      target: { value: inputValue },
    } = e;
    const trimValue = trim(inputValue);
    inputValue = trimValue;
    const data = {
      value: inputValue,
      touched: validate(inputValue, { isRequired: true, isEmail: true }),
      valid: validate(inputValue, { isRequired: true, isEmail: true }),
    };
    callBack(data);
  };

  const handleOnBlur = (e, callBack) => {
    let {
      target: { value: inputValue },
    } = e;
    const trimValue = trim(inputValue);
    inputValue = trimValue;
    const valid = validate(inputValue, { isRequired: true, isEmail: true });
    setEmailStatus(inputValue, valid, true, emailValidateMessage, callBack);
  };

  const handleOnRecipientEmailChange = e => {
    let {
      target: { value: inputValue },
    } = e;
    inputValue = trim(inputValue);
    if (WHITESPACE_REGEX.test(inputValue)) {
      const emailCount = split(inputValue, ',');
      const emailCountStatus = emailCount.length > RECIPIENT_MAX_EMAIL_ACCEPT;
      inputValue = emailCountStatus ? inputRecipientEmail.value : inputValue;
      const data = {
        value: inputValue,
        touched: validate(inputValue, { isRequired: true }),
        valid: validate(inputValue, { isRequired: true }),
      };
      setRecipientEmail(data);
    }
  };

  const handleRecipientEmailOnBlur = e => {
    e.preventDefault();
    let {
      target: { value: inputValue },
    } = e;
    inputValue = trim(inputValue);
    const emailCount = split(inputValue, ',');
    const emailCountStatus = emailCount.length > 10;
    let { valid: validStatus } = inputRecipientEmail;
    inputValue = emailCountStatus ? inputRecipientEmail.value : inputValue;
    if (emailCount.length > 0 && !isEmpty(inputValue)) {
      forEach(emailCount, item => {
        const emailValue = trim(item);
        const status = validate(emailValue, { isRequired: true, isEmail: true });
        if (!status) {
          validStatus = status;
          return false;
        }
        return validStatus;
      });
    } else {
      validStatus = validate(inputValue, { isRequired: true, isEmail: true });
    }
    const data = {
      value: inputValue,
      touched: true,
      valid: validStatus,
      errorMsg: emailValidateMessage,
    };
    setRecipientEmail(data);
  };

  const handleTextAreaChange = e => {
    let {
      target: { value: inputValue },
    } = e;
    const trimValue = trim(inputValue);
    inputValue = !isEmpty(trimValue) ? inputValue : trimValue;
    setShareCartMessage(inputValue);
  };

  const getTermsAndCondition = () => (
    <TermsConditions
      termsConditions={shareCartTermsCondition}
      termsOfUseLink={termsOfUseLink}
      termsOfUse={termsOfUse}
      watersPrivacyNoticeLink={watersPrivacyNoticeLink}
      watersPrivacyNotice={watersPrivacyNotice}
      target={isEProcurementApp() ? '_self' : '_blank'}
    />
  );

  const getEmailEqualStatus = (senderEmail, recipientEmail) => {
    let status = toLower(senderEmail) === toLower(recipientEmail);
    if (!status && !isEmpty(recipientEmail)) {
      const splitEmail = split(recipientEmail, ',');
      forEach(splitEmail, item => {
        const value = trim(item);
        if (toLower(senderEmail) === toLower(value)) {
          status = true;
        }
      });
    }
    return status;
  };

  const shareCartValidate = () => {
    const { valid: isSenderEmailValid, value: senderEmail } = inputSenderEmail;
    const { valid: isRecipientEmailValid, value: recipientEmail } = inputRecipientEmail;
    const { valid: isReCaptchaValid } = reCaptchaStatus;
    let status = true;
    if (!isReCaptchaValid) {
      setReCaptchaStatus({ valid: false, showErrorMsg: true });
      status = false;
    }
    if (!isSenderEmailValid) {
      setEmailStatus(senderEmail, isSenderEmailValid, true, emailValidateMessage, setSenderEmail);
      status = false;
    }

    if (!isRecipientEmailValid) {
      const errorMsg =
        !isEmpty(recipientEmail) && getEmailEqualStatus(senderEmail, recipientEmail)
          ? shareCartSameEmailMessage
          : emailValidateMessage;
      setEmailStatus(recipientEmail, isRecipientEmailValid, true, errorMsg, setRecipientEmail);
      status = false;
    }

    if (
      isSenderEmailValid &&
      isRecipientEmailValid &&
      getEmailEqualStatus(senderEmail, recipientEmail)
    ) {
      setEmailStatus(recipientEmail, false, true, shareCartSameEmailMessage, setRecipientEmail);
      status = false;
    }
    return status;
  };

  const onShareCartButtonSubmit = e => {
    e.preventDefault();
    const { value: senderEmail } = inputSenderEmail;
    const { value: recipientEmail } = inputRecipientEmail;
    const status = shareCartValidate();
    if (status) {
      const products = [];
      forEach(cartItems, item => {
        const { product = {}, quantity } = item;
        const { code, name } = product;
        const basePriceValue = getBasePriceValue(item, includeTaxWithPrice);
        const customerPriceValue = getCustomerPriceValue(item, includeTaxWithPrice);
        let unitPrice = get(basePriceValue, 'formattedValue', '--');
        if (isUserLoggedIn) {
          unitPrice = get(customerPriceValue, 'formattedValue') || unitPrice;
        }
        products.push({ id: code, name, quantity, unitPrice });
      });
      const data = {
        senderEmail,
        recipientEmail,
        message: shareCartMessage,
        products,
      };
      onShareCartSubmit(data);
    }
  };

  return (
    <Suspense fallback={<div />}>
      <form className="share-cart-form row" data-locator="share-cart-form">
        <div className="col-xs-12" data-locator="required-text">
          <RequiredText requiredField={requiredField} />
        </div>
        <div className="col-lg-6 col-sm-6 col-xs-12">
          <div className="share-cart-form-label" data-locator="label-your-email-address">
            {getLabelsWithAsterisk(yourEmailAddress, true)}
          </div>
          <InputWithIcon
            id="yourEmailAddress"
            type="email"
            name="yourEmailAddress"
            maxLength={EMAIL_INPUT_MAX_LENGTH}
            className="share-cart-input-field"
            autoComplete="email"
            {...inputSenderEmail}
            {...{
              onChange: e => {
                handleOnChange(e, setSenderEmail);
              },
              onBlur: e => {
                handleOnBlur(e, setSenderEmail);
              },
            }}
            elementLocator="input-your-email-address"
          />
        </div>
        <div className="col-lg-6 col-sm-6 col-xs-12">
          <div className="share-cart-form-label" data-locator="label-recipient-email-address">
            {getLabelsWithAsterisk(recipientEmailAddress, true)}
          </div>
          <TextArea
            {...inputRecipientEmail}
            id="recipientEmailAddress"
            name="recipientEmailAddress"
            placeholder={recipientEmailPlaceholder}
            className="share-cart-input-email-field"
            autoComplete="shipping email"
            elementLocator="input-recipient-email-address"
            title="recipientEmailAddress"
            maxlength={RECIPIENT_EMAIL_MAX_LENGTH}
            {...{
              onChange: e => {
                handleOnRecipientEmailChange(e);
              },
              onBlur: e => {
                handleRecipientEmailOnBlur(e);
              },
            }}
          />
          {inputRecipientEmail.touched && !inputRecipientEmail.valid && (
            <span
              className="col-sm-12 error-msg"
              data-locator="input-recipient-email-address-error"
            >
              {inputRecipientEmail.errorMsg}
            </span>
          )}
          <div data-locator="multiple-email-address-info-message">{multipleEmailMessage}</div>
        </div>
        <div className="col-lg-6 col-sm-6 col-xs-12">
          <div className="share-cart-form-label" data-locator="label-share-cart-message">
            {`${message} (${optional})`}
          </div>
          <TextArea
            className="share-cart-message"
            placeholder=""
            title="TextArea"
            {...{
              onChange: e => {
                handleTextAreaChange(e);
              },
            }}
            value={shareCartMessage}
            elementLocator="text-area-share-cart-message"
            maxlength={200}
            resize={false}
          />
        </div>
        <div className="col-lg-6 col-sm-6 col-xs-12">
          <div className="captcha-section" data-locator="share-cart-re-captcha">
            <Captcha isoCode={isoCode} setReCaptchaStatus={setReCaptchaStatus} />
          </div>
          {!reCaptchaStatus.valid && reCaptchaStatus.showErrorMsg && (
            <span className="error-msg">{captchaErrorMessage}</span>
          )}
          {getTermsAndCondition()}
        </div>
        <div className="col-lg-6 col-sm-6 col-xs-12 share-cart-modal-button">
          <Button
            className="primary share-cart-button"
            elementLocator="button-share-cart-submit"
            {...{ onClick: e => onShareCartButtonSubmit(e) }}
          >
            {shareCart}
          </Button>

          <Anchor
            to="#"
            className="share-cart-link"
            elementLocator="link-share-cart-cancel"
            {...{ onClick: () => setShowShareCartModal(false) }}
          >
            {cancel}
          </Anchor>
        </div>
      </form>
    </Suspense>
  );
};

ShareCartForm.propTypes = {
  labels: PropTypes.objectOf(PropTypes.string),
  config: PropTypes.objectOf(PropTypes.string),
  onShareCartSubmit: PropTypes.func,
  setShowShareCartModal: PropTypes.func,
  isoCode: PropTypes.string,
  userEmail: PropTypes.string,
  cartItems: PropTypes.objectOf(PropTypes.string),
  includeTaxWithPrice: PropTypes.bool,
  isUserLoggedIn: PropTypes.bool,
};

ShareCartForm.defaultProps = {
  labels: {},
  config: {},
  cartItems: {},
  isoCode: '',
  userEmail: '',
  includeTaxWithPrice: false,
  isUserLoggedIn: false,
  onShareCartSubmit: () => {
    /* default onShareCartSubmit handler */
  },
  setShowShareCartModal: () => {
    /* default setShowShareCartModal handler */
  },
};

export default ShareCartForm;
